<template>
    <div id="skew-chart" 
        class="base-popup monitor-graph" 
    >
        <v-card v-if="Object.keys(skew_options)"
            class="base-popup__wrapper" 
        >
            <div id="base-popup__capture_range">
                <div class="base-popup__content">
                    <div class="base-popup__content-detail d-flex align-center justify-space-between">
                        <span>{{ skew_options.title }}</span>
                        <span>{{ latlng_text }}</span>
                        <div>
                            <v-btn
                                id="tsgraph-download" 
                                @click="save_png"
                                icon
                            >
                                <v-icon color="black">
                                    icon-file-download
                                </v-icon>
                            </v-btn>
                            <v-btn
                                class="close-btn"
                                color="black"
                                icon
                                @click="close_dialog"
                            >
                                <v-icon color="black">
                                    mdi-close
                                </v-icon>
                            </v-btn>
                        </div>
                    </div>
                    <div class="base-popup__content-graph">
                        <div class="svg_container">
                            <svg />
                        </div>
                    </div>
                </div>
            </div>
        </v-card>
        <div v-else-if="error_text">
            <v-alert
                :value="true"
                type="info"
                class="error__box"
                color="#ee3934"
            >
                {{ error_text }}
                <v-btn
                    class="error__close-btn"
                    color="white"
                    icon
                    @click="close_dialog"
                >
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-alert>
        </div>
        <LoadingHint v-else
            :text="$t('__chart_common.loading_text')"
        />
    </div>
</template>

<script>
    import { get_remote_file_ajax } from "@/utils/emap/emap_init.js"
    import { take_screenshot } from '@/utils/common/screenshot.js'
    import { mapGetters } from "vuex";
    import * as d3 from 'd3';
    import LoadingHint from '@/components/common/loading_hint.vue'
    export default {
        name: 'SkewChart',

        components: { 
            LoadingHint,
        },


        props: {
            skew_options: {
                type: Object,
                required: true,
            }
        },

        data() {
            return {
                error_text: '',
                alldata: {},
            }    
        },

        computed: {
            ...mapGetters([
                'is_mobile_width',
                'is_pad_width',
            ]),

            latlng_text() {
                const [lat, lon] = this.skew_options.latlng
                return `[${lat}°N, ${lon}°E]`
            }
        },

        async mounted() {
            const res = await get_remote_file_ajax(this.skew_options.url)
            const json = res.data
            const alldata = this.parseDataNew(json); //get alldata
            this.drawSkew(this.skew_options, alldata)
        },

        methods: {
            async drawSkew(Options, alldata) {
                //main_svg
                var main_svg_h = screen.height * 0.55;
                var main_svg_w = main_svg_h * 1.875;

                //標題
                var T_w = main_svg_w;
                var T_h = 50;

                //斜溫圖 .R_svg
                var R_m = [35, 70, 35, 35] //上右下左
                var R_svg_w = main_svg_w * 0.53
                var R_svg_h = main_svg_h - 50
                var R_w = R_svg_w - R_m[1] - R_m[3]
                var R_h = R_svg_h - R_m[0] - R_m[2]
                var R_barbs_m_r = 30; //風標與斜溫圖的距離

                //風徑圖 .C_svg
                var C_m = [45, 0, 50, 0]
                const C_w = main_svg_h - 50 //風徑圖寬高, 正方形
                const C_r = C_w * 0.43 //風徑圖半徑
                const C_max_value = 110 //風徑圖最大數值

                var deg2rad = (Math.PI / 180);
                var tan = Math.tan(55 * deg2rad)
                const basep = 1050
                const topp = 100
                const plines = [1000, 850, 700, 500, 300, 200, 100]
                const pticks = [950, 900, 800, 750, 650, 600, 550, 450, 400, 350, 250, 150];
                const barbsize = 25;

                // Scales and axes. Note the inverted domain for the y-scale: bigger is up!
                var x = d3.scaleLinear().range([0, R_w]).domain([-45, 50])
                const y = d3.scaleLog().range([0, R_h]).domain([topp, basep])

                const r = d3.scaleLinear().range([0, C_r]).domain([0, C_max_value])
                const y2 = d3.scaleLinear()
                const xAxis = d3.axisBottom().scale(x).tickSize(0, 0).ticks(10)
                const yAxis = d3.axisLeft().scale(y).tickSize(0, 0).tickValues(plines)
                    .tickFormat(d3.format(".0d"))
                const yAxis2 = d3.axisRight().scale(y).tickSize(5, 0).tickValues(pticks); // just for ticks

                // initial skewT_main_svg
                const main_svg = d3.select(".svg_container svg")
                    .attr("width", main_svg_w)
                    .attr("height", main_svg_h)
                    .attr("class", "skewT_main_svg");

                // various path generators
                var line = d3.line()
                    // .interpolate("linear")
                    .x(function (d, i) {
                        return x(d.temp) + (y(basep) - y(d.press)) / tan;
                    })
                    //.x(function(d,i) { return x(d.temp); })
                    .y(function (d, i) {
                        return y(d.press);
                    });

                var line2 = d3.line()
                    // .interpolate("linear")
                    .x(function (d, i) {
                        return x(d.dwpt) + (y(basep) - y(d.press)) / tan;
                    })
                    .y(function (d, i) {
                        return y(d.press);
                    });

                var hodoline = d3.lineRadial()
                    .radius(function (d) {
                        return r(d.wspd);
                    })
                    .angle(function (d) {
                        return (d.wdir + 180) * (Math.PI / 180);
                    });

                // bisector function for tooltips
                var bisectTemp = d3.bisector(function (d) {
                    return d.press;
                }).left;

                // 斜溫圖svg
                var svg = main_svg.append("svg")
                    .attr("width", R_svg_w)
                    .attr("height", R_svg_h)
                    .attr("y", T_h)
                    .attr("class", "R_svg")
                    .append("g")
                    .attr("transform", "translate(" + R_m[3] + "," + R_m[0] + ")");

                // 風徑圖svg
                var svghodo = main_svg.append("svg")
                    .attr("width", C_w)
                    .attr("height", C_w)
                    .attr("x", R_svg_w)
                    .attr("y", T_h)
                    .attr("class", "C_svg")
                    .append("g")
                    .attr("transform", "translate(" + C_w / 2 + "," + C_w / 2 + ")");

                makeBarbTemplates();
                drawBackground();

                var skewtgroup = svg.append("g").attr("class", "skewt"); // put skewt lines in this group
                var barbgroup = svg.append("g").attr("class", "windbarb"); // put barbs in this group
                var hodogroup = svghodo.append("g").attr("class", "hodo"); // put hodo stuff in this group
                drawToolTips();

                const hodobarbstest = []
                const tlinetest = []
                const interpdots = []
                const barbstest = []
                
                var temp = []
                var temp2 = [] 
                var temp3 = []
                const parsedCSV = alldata.filter(function (d) {
                    return (d.temp > -1000 && d.dwpt > -1000 && [d.pres, d.hght, d.temp, d.dwpt].indexOf(null) === -1);
                });
                const barbs = alldata.filter(function (d) { //風標 氣壓>=100,
                    return (d.press >= topp && [d.wdir, d.wspd].indexOf(null) === -1);
                });
                const hodobarbs = alldata.filter(function (d) { //風徑圖 氣壓>=100,
                    return (d.press >= topp && [d.wdir, d.wspd].indexOf(null) === -1);
                });
                //parsedCSVreversed = parsedCSV.reverse(); // bisector needs ascending array
                const interpdot = [hodobarbs[0], hodobarbs[hodobarbs.length - 1]];

                temp.push(hodobarbs);
                temp2.push(parsedCSV);
                temp3.push(barbs);
                interpdots.push(interpdot);
                hodobarbstest.push(temp);
                tlinetest.push(temp2);
                barbstest.push(temp3); //風向


                // need this for dots for some reason
                //  mouseoverdata = tlinetest[0][10].slice(0).reverse();
                const mouseoverdata = tlinetest[0][0].slice(0).reverse();
                const flattened = interpdots.reduce(function (a, b) {
                    return a.concat(b);
                });
                drawFirstHour();

                if (Options.callback) {
                    Options.callback();
                }


                function makeBarbTemplates() {
                    var speeds = d3.range(5, 105, 5);
                    const barbdef = svg.append('defs')
                    speeds.forEach(function (d) {
                        var thisbarb = barbdef.append('g').attr('id', 'barb' + d);

                        var flags = Math.floor(d / 50);
                        var pennants = Math.floor((d - flags * 50) / 10);
                        var halfpennants = Math.floor((d - flags * 50 - pennants * 10) / 5);
                        var px = barbsize;

                        // Draw wind barb stems
                        thisbarb.append("line").attr("x1", 0).attr("x2", 0).attr("y1", 0).attr("y2", barbsize);

                        // Draw wind barb flags and pennants for each stem
                        for (let i = 0; i < flags; i++) {
                            thisbarb.append("polyline")
                                .attr("points", "0," + px + " -10," + (px) + " 0," + (px - 4))
                                .attr("class", "flag");
                            px -= 7;
                        }
                        // Draw pennants on each barb
                        for (let i = 0; i < pennants; i++) {
                            thisbarb.append("line")
                                .attr("x1", 0)
                                .attr("x2", -10)
                                .attr("y1", px)
                                .attr("y2", px + 4)
                            px -= 3;
                        }
                        // Draw half-pennants on each barb
                        for (let i = 0; i < halfpennants; i++) {
                            thisbarb.append("line")
                                .attr("x1", 0)
                                .attr("x2", -5)
                                .attr("y1", px)
                                .attr("y2", px + 2)
                            px -= 3;
                        }
                    });
                }

                function drawFirstHour() {
                    // draw initial set of lines
                    const tlines = skewtgroup.selectAll("tlines")
                        .data(tlinetest[0]).enter().append("path")
                        .attr("class", function (d, i) {
                            return (i < 10) ? "temp member" : "temp mean"
                        })
                        .attr("clip-path", "url(#clipper)")
                        .attr("d", line);

                    const tdlines = skewtgroup.selectAll("tdlines")
                        .data(tlinetest[0]).enter().append("path")
                        .attr("class", function (d, i) {
                            return (i < 10) ? "dwpt member" : "dwpt mean"
                        })
                        .attr("clip-path", "url(#clipper)")
                        .attr("d", line2);

                    const holines = hodogroup.selectAll("hodolines")
                        .data(hodobarbstest[0]).enter().append("path")
                        .attr("class", function (d, i) {
                            return (i < 10) ? "hodoline member" : "hodoline mean"
                        })
                        .attr("d", hodoline);

                    // drop point && end point
                    const hododots = hodogroup.selectAll('hododots')
                        .data(flattened.slice(0, 55)).enter().append("circle")
                        .attr("r", function (d, i) {
                            return (i < 50) ? 2 : 4
                        })
                        .attr("cx", function (d, i) {
                            return r(d.wspd * Math.sin((180 + d.wdir) * deg2rad));
                        })
                        .attr("cy", function (d, i) {
                            return -r(d.wspd * Math.cos((180 + d.wdir) * deg2rad));
                        })
                        .attr("class", function (d, i) { //氣壓值高的話 drop, 低的畫 end
                            return "hododot hgt" + ((d.press < 500) ? "_drop" : "_end");
                        });

                    const allbarbs = barbgroup.selectAll("barbs")
                        .data(barbstest[0][0]).enter().append("use")
                        .attr("xlink:href", function (d) {
                            return "#barb" + d.wspdround;
                        })
                        .attr("transform", function (d, i) { //風向往右畫
                            return "translate(" + (R_w + R_barbs_m_r) + "," + y(d.press) + ") rotate(" + (d.wdir + 180) + ")";
                        });
                }

                function drawToolTips() {
                    // Draw T/Td tooltips
                    const focus = skewtgroup.append("g").attr("class", "focus tmpc").style("display", "none");
                    focus.append("circle").attr("r", 4);
                    focus.append("text").attr("x", 9).attr("dy", ".35em");

                    const focus2 = skewtgroup.append("g").attr("class", "focus dwpc").style("display", "none");
                    focus2.append("circle").attr("r", 4);
                    focus2.append("text").attr("x", -9).attr("text-anchor", "end").attr("dy", ".35em");

                    const focus3 = skewtgroup.append("g").attr("class", "focus").style("display", "none");
                    focus3.append("text").attr("x", 0).attr("text-anchor", "start").attr("dy", ".35em");

                    svg.append("rect")
                        .attr("class", "overlay")
                        .attr("width", R_w)
                        .attr("height", R_h)
                        .on("mouseover", function () {
                            focus.style("display", null);
                            focus2.style("display", null);
                            focus3.style("display", null);
                        })
                        .on("mouseout", function () {
                            focus.style("display", "none");
                            focus2.style("display", "none");
                            focus3.style("display", "none");
                        })
                        .on("mousemove", mousemove);

                    function mousemove() {
                        var y0 = y.invert(d3.mouse(this)[1]); // get y value of mouse pointer in pressure space
                        var i = bisectTemp(mouseoverdata, y0, 1, mouseoverdata.length - 1);
                        var d0 = mouseoverdata[i - 1];
                        var d1 = mouseoverdata[i];
                        var d = y0 - d0.press > d1.press - y0 ? d1 : d0;
                        focus.attr("transform", "translate(" + (x(d.temp) + (y(basep) - y(d.press)) / tan) + "," + y(d.press) + ")");
                        focus2.attr("transform", "translate(" + (x(d.dwpt) + (y(basep) - y(d.press)) / tan) + "," + y(d.press) + ")");
                        focus3.attr("transform", "translate(0," + y(d.press) + ")");
                        focus.select("text").text(Math.round(d.temp * 10) / 10 + "°C");
                        focus2.select("text").text(Math.round(d.dwpt * 10) / 10 + "°C");
                        focus3.select("text").text("--" + (Math.round(d.hghtagl / 100) / 10) + "km");
                    }
                }

                function drawBackground() { //skewT_main_svg
                    var svg = d3.select(".skewT_main_svg .R_svg g").append("g").attr("class", "skewtbg");
                    var svghodo = d3.select(".skewT_main_svg .C_svg g").append("g").attr("class", "hodobg");

                    var dryline = d3.line()
                        // .interpolate("linear")
                        .x(function (d, i) {
                            return x((273.15 + d) / Math.pow((1000 / pp[i]), 0.286) - 273.15) + (y(basep) - y(pp[i])) / tan;
                        })
                        .y(function (d, i) {
                            return y(pp[i])
                        });

                    // Add clipping path
                    svg.append("clipPath")
                        .attr("id", "clipper")
                        .append("rect")
                        .attr("x", 0)
                        .attr("y", 0)
                        .attr("width", R_w)
                        .attr("height", R_h);

                    // Skewed temperature lines
                    svg.selectAll("gline")
                        .data(d3.range(-100, 45, 10))
                        .enter().append("line")
                        .attr("x1", function (d) {
                            return x(d) - 0.5 + (y(basep) - y(100)) / tan;
                        })
                        //.attr("x1", function(d) { return x(d)-0.5; })
                        .attr("x2", function (d) {
                            return x(d) - 0.5;
                        })
                        .attr("y1", 0)
                        .attr("y2", R_h)
                        .attr("class", function (d) {
                            if (d == 0) {
                                return "tempzero";
                            } else {
                                return "gridline"
                            }
                        })
                        .attr("clip-path", "url(#clipper)");
                    //.attr("transform", "translate(0," + R_h + ") skewX(-30)");

                    // Logarithmic pressure lines
                    svg.selectAll("gline2")
                        .data(plines)
                        .enter().append("line")
                        .attr("x1", 0)
                        .attr("x2", R_w)
                        .attr("y1", function (d) {
                            return y(d);
                        })
                        .attr("y2", function (d) {
                            return y(d);
                        })
                        .attr("class", "gridline");

                    // create array to plot dry adiabats
                    var pp = d3.range(topp, basep + 1, 10);
                    var dryad = d3.range(-30, 240, 20);
                    var all = [];
                    for (let i = 0; i < dryad.length; i++) {
                        var z = [];
                        for (let j = 0; j < pp.length; j++) {
                            z.push(dryad[i]);
                        }
                        all.push(z);
                    }

                    // Draw dry adiabats
                    svg.selectAll(".dryline")
                        .data(all)
                        .enter().append("path")
                        .attr("class", "gridline")
                        .attr("clip-path", "url(#clipper)")
                        .attr("d", dryline);

                    // Line along right edge of plot
                    svg.append("line")
                        .attr("x1", R_w - 0.5)
                        .attr("x2", R_w - 0.5)
                        .attr("y1", 0)
                        .attr("y2", R_h)
                        .attr("class", "gridline");

                    // 風徑圖背景圓圈
                    svghodo.selectAll(".circles")
                        .data(d3.range(10, C_max_value, 10))
                        .enter().append("circle")
                        .attr("cx", 0)
                        .attr("cy", 0)
                        .attr("r", function (d) {
                            return r(d);
                        })
                        .attr("class", "gridline");
                    svghodo.selectAll("hodolabels")
                        .data(d3.range(10, C_max_value, 20)).enter().append("text")
                        .attr('x', 0)
                        .attr('y', function (d, i) {
                            return r(d);
                        })
                        .attr('dy', '0.4em')
                        .attr('class', 'hodolabels')
                        .attr('text-anchor', 'middle')
                        .text(function (d) {
                            return d + ' kts';
                        });

                    // Add axes
                    svg.append("g").attr("class", "x axis").attr("transform", "translate(0," + (R_h - 0.5) + ")").call(xAxis);
                    svg.append("g").attr("class", "y axis").attr("transform", "translate(-0.5,0)").call(yAxis);
                    svg.append("g").attr("class", "y axis ticks").attr("transform", "translate(-0.5,0)").call(yAxis2);
                    //svg.append("g").attr("class", "y axis hght").attr("transform", "translate(0,0)").call(yAxis2);

                    // 浮水印
                    var img_w = 85;
                    main_svg.append("image")
                        .attr("xlink:href", "/static/images/capture/blue_cwa_logo_string_eng.png")
                        .attr("width", img_w)
                        .attr("x", T_w - img_w - 5)
                        .attr("y", 5);

                    // 斜溫圖標題
                    svg.append("text")
                        .attr("x", (R_w + R_m[3]) / 2)
                        .attr("y", -(R_m[0] / 2))
                        .attr("text-anchor", "middle")
                        .style("font-size", "1.3em")
                        .text("Skew T - log P");
                    // 斜溫圖左方氣壓單位
                    svg.append("text")
                        .attr("x", -30)
                        .attr("y", -15)
                        .attr("font-size", "0.8em")
                        .text("P (hPa)");
                    // 斜溫圖下方溫度單位
                    svg.append("text")
                        .attr("x", R_w / 2)
                        .attr("y", R_h + R_m[0])
                        .attr("font-size", "0.8em")
                        .attr("text-anchor", "middle")
                        .text("T (℃)");

                    // 斜溫圖風速速度單位
                    svg.append("text")
                        .attr("x", R_w + R_barbs_m_r)
                        .attr("y", -10)
                        .attr("font-size", "0.8em")
                        .attr("text-anchor", "middle")
                        .text("Wind (kts)");
                    // 斜溫圖風速底直線
                    svg.append('line')
                        .attr('x1', R_w + R_barbs_m_r).attr('y1', 0)
                        .attr('x2', R_w + R_barbs_m_r).attr('y2', R_h)
                        .style('stroke', '#bbb')
                        .style('stroke-width', 1);

                    // 風徑圖標題
                    svghodo.append("text")
                        .attr("x", 0)
                        .attr("y", -C_w / 2 + 18)
                        .attr("text-anchor", "middle")
                        .style("font-size", "1.3em")
                        .text("Hodograph");
                    // 風徑圖方向 N
                    svghodo.append("text")
                        .attr("x", 0)
                        .attr("y", -C_r + 13)
                        .attr("text-anchor", "middle")
                        .style("font-size", "1em")
                        .style("fill", "#999")
                        .text("N");
                    // 風徑圖底直線
                    svghodo.append('line')
                        .attr('x1', -r(100)).attr('y1', 0)
                        .attr('x2', r(100)).attr('y2', 0)
                        .style('stroke', '#bbb')
                        .style('stroke-width', 1);
                    // 風徑圖底橫線
                    svghodo.append('line')
                        .attr('x1', 0).attr('y1', -r(100))
                        .attr('x2', 0).attr('y2', r(100))
                        .style('stroke', '#bbb')
                        .style('stroke-width', 1);

                    // 風徑圖圖例 Drop point圖例
                    svghodo.append("circle")
                        .attr("r", 2)
                        .attr("cx", (C_w / 2) - 85)
                        .attr("cy", (C_w / 2) - 44)
                        .attr("class", "hododot hgt_drop");
                    // 風徑圖圖例 End point圖例
                    svghodo.append("circle")
                        .attr("r", 2)
                        .attr("cx", (C_w / 2) - 85)
                        .attr("cy", (C_w / 2) - 29)
                        .attr("class", "hododot hgt_end");

                    // 風徑圖圖例 Drop point文字
                    svghodo.append("text")
                        .attr("x", (C_w / 2) - 80)
                        .attr("y", (C_w / 2) - 40)
                        .style("font-size", "0.8em")
                        .text("Drop point");
                    // 風徑圖圖例 End point文字
                    svghodo.append("text")
                        .attr("x", (C_w / 2) - 80)
                        .attr("y", (C_w / 2) - 25)
                        .style("font-size", "0.8em")
                        .text("End point");
                }
            },

            
            parseDataNew(json) {
                return $.map(json['pres'], function (press, i) {
                    return {
                        press: press,
                        hght: json.hght[i],
                        temp: json.tmpc[i],
                        dwpt: json.dwpc[i],
                        wdir: json.wdir[i],
                        wspd: json.wspd[i],
                        hghtagl: json.hght[i] - json.hght[0],
                        wspdround: Math.round(json.wspd[i] / 5) * 5
                    };
                });
            },

            close_dialog() {
                this.$emit('close-chart-dialog');
            },

            save_png() {
                const { title, latlng } = this.skew_options
                const [ lat, lon ]  = latlng
                const file_name = `SkewT_${title}_${lon}E_${lat}N`
                const node = document.querySelector('#base-popup__capture_range')
                take_screenshot(node, file_name)
            },
        }
    }
</script>

<style lang="scss" scoped>
    @import '../../../../css/base_popup';
</style>

<style>
    .skewT_main_svg .axis path,
    .skewT_main_svg .axis line {
        fill: none;
        stroke: #000;
        stroke-width: 2.0px;
        shape-rendering: crispEdges;
    }
    .skewT_main_svg .x.axis path {  }
    .skewT_main_svg .y.axis path {  }
    .skewT_main_svg .axis { fill: #000; }
    .skewT_main_svg .y.axis { font-size: 13px; }
    .skewT_main_svg .y.axis.hght { font-size: 9px; fill: red;}
    .skewT_main_svg .x.axis { font-size: 13px; }
    .skewT_main_svg .y.axis.ticks text { display: none; }

    .skewT_main_svg .temp {
        fill: none;
        stroke: red;
        stroke-width: 2px;
    }
    .skewT_main_svg .dwpt {
        fill: none;
        stroke: green;
        stroke-width: 2px;
    }
    .skewT_main_svg .member   { stroke-width: 2.5px; }
    .skewT_main_svg .mean     { stroke-width: 2.5px; }
    .skewT_main_svg .hodoline { stroke: #000; opacity: 1.0; stroke-width: 1.5px; fill: none;}
    .skewT_main_svg .hodoline.mean { stroke: #000; opacity: 1.0; stroke-width: 3.5px;}
    .skewT_main_svg .hododot {  stroke: #000; opacity: 0.8; }

    .skewT_main_svg .hododot.hgt_drop { fill: red; }
    .skewT_main_svg .hododot.hgt_end { fill: blue; }

    .skewT_main_svg .gridline, .tempzero {
        stroke: #dfdfdf;
        stroke-width: 1.00px;
        fill: none;
    }
    .skewT_main_svg .tempzero { stroke: #aaa; stroke-width: 1.50px; }

    .skewT_main_svg .barline { stroke: #000; stroke-width: 0.5px; }
    .skewT_main_svg .rectline { fill: #aaa; opacity: 0.5;}
    .skewT_main_svg .rectline2 { fill: #000; }
    .skewT_main_svg .index { font-size: 13px; }
    .skewT_main_svg .header { font-weight: bold; font-size: 11px;}
    .skewT_main_svg .hodolabels { font-size: 12px; opacity: 0.5;}
    .skewT_main_svg .lcltext { font-size: 11px; }
    .skewT_main_svg .key { font-size: 10px; }

    .skewT_main_svg .windbarb { stroke: #000; stroke-width: 1.00px; fill: none; }
    .skewT_main_svg .flag { fill: #000; }

    .skewT_main_svg .overlay {
        fill: none;
        pointer-events: all;
    }

    .skewT_main_svg .focus.tmpc circle { fill: red;   stroke: none; }
    .skewT_main_svg .focus.dwpc circle { fill: green; stroke: none; }
    .skewT_main_svg .focus text { font-size: 12px; }

    .skewT_main_svg div.rollover { padding: 15px 5px 15px 5px; display: inline; cursor: pointer;}
    .skewT_main_svg div.rollover.selected { background-color: #EFEFEF; border-bottom: 2px solid gray;}
</style>